import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { isAuthNotReadyError } from "@/lib/utils";
import i18n from "@/lib/i18n";

/** Browser representation of a durable notification_recipients row. */
export interface InboxNotification {
  id: string;
  eventId: string;
  tenantId: string;
  userId: string;
  title: string | null;
  body: string | null;
  actionUrl?: string | null;
  reminderTranslation?: { titleKey: string; bodyKey: string; values: { task: string; date: string } };
  sessionId: string | null;
  runId: string | null;
  agentId: string | null;
  priority: number;
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
  eventType: string;
  occurredAt: string;
  /** Absent when the bridge predates completed-reminder history links. */
  scheduleHistoryUrl?: string;
}

export type InboxConnectionStatus = "loading" | "ready" | "stale" | "error";
export type InboxNotificationView = "unread" | "all";

export interface InboxNotificationAPI {
  notifications: InboxNotification[];
  /** Uses the same authenticated, tenant-scoped fetch path as inbox polling. */
  fetchScheduleHistory?: (url: string) => Promise<Response>;
  /** The server-side inbox filter currently shown in the dropdown. */
  view: InboxNotificationView;
  setView: (view: InboxNotificationView) => void;
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  /** Opaque server cursor for the next page in the active view. */
  nextCursor: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  /** Fresh data, stale prior data, or a failed initial load. */
  status: InboxConnectionStatus;
  error: string | null;
  /** True after at least one successful durable inbox connection. */
  isAvailable: boolean;
  /** Increments after a successful refresh; consumers may retry work then. */
  refreshGeneration: number;
  refresh: () => Promise<void>;
  markRead: (notificationId: string) => Promise<boolean>;
  dismiss: (notificationId: string) => Promise<boolean>;
  markReadBySession: (sessionId: string) => Promise<boolean>;
}

interface InboxPageResponse {
  notifications?: InboxNotification[];
  next_cursor?: unknown;
}

const TIER_ACTIVE_MS = 20_000;
const TIER_QUIET_MS = 60_000;
const TIER_HIDDEN_MS = 60_000;
const TIER_AUTH_NOT_READY_MS = 1_500;
const TIER_RETRYABLE_ERROR_MS = 20_000;
const INBOX_PAGE_SIZE = 50;

class InboxRefreshError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = "InboxRefreshError";
  }
}

function isRetryableInboxError(err: unknown): boolean {
  return err instanceof InboxRefreshError && err.retryable;
}

function responseError(resource: string, response: Response): InboxRefreshError {
  const retryable = response.status === 408 || response.status === 429 || response.status >= 500;
  return new InboxRefreshError(
    `${resource} request failed (${response.status})`,
    retryable,
  );
}

function inboxPageUrl(view: InboxNotificationView, cursor?: string | null): string {
  const cursorParam = cursor ? `&cursor=${encodeURIComponent(cursor)}` : "";
  return `/api/notifications?state=${view}&limit=${INBOX_PAGE_SIZE}${cursorParam}`;
}

function inboxPageFromResponse(data: InboxPageResponse): {
  notifications: InboxNotification[];
  nextCursor: string | null;
} {
  return {
    notifications: Array.isArray(data.notifications) ? data.notifications : [],
    nextCursor: typeof data.next_cursor === "string" && data.next_cursor.length > 0
      ? data.next_cursor
      : null,
  };
}

function sameNotificationIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/**
 * Inbox events are durable rows, but the UI's unit of attention is a session.
 * Keep the newest unread event for each session so a second terminal run
 * refreshes its session notification instead of adding another outstanding
 * item. Read history and rows without a session remain distinct.
 */
function dedupeNotifications(notifications: InboxNotification[]): InboxNotification[] {
  const seenIds = new Set<string>();
  const deduped: InboxNotification[] = [];
  const unreadSessionIndexes = new Map<string, number>();

  for (const notification of notifications) {
    if (seenIds.has(notification.id)) continue;
    seenIds.add(notification.id);

    if (notification.readAt === null && notification.sessionId) {
      const existingIndex = unreadSessionIndexes.get(notification.sessionId);
      if (existingIndex !== undefined) {
        const existing = deduped[existingIndex];
        const existingTime = Date.parse(existing.occurredAt || existing.createdAt);
        const notificationTime = Date.parse(notification.occurredAt || notification.createdAt);
        // API pages are newest-first. Only replace that first row when an
        // incoming row proves newer, which also makes pagination resilient to
        // an out-of-order response.
        if (Number.isFinite(notificationTime) && (!Number.isFinite(existingTime) || notificationTime > existingTime)) {
          deduped[existingIndex] = notification;
        }
        continue;
      }
      unreadSessionIndexes.set(notification.sessionId, deduped.length);
    }

    deduped.push(notification);
  }

  return deduped;
}

function appendNotifications(
  current: InboxNotification[],
  nextPage: InboxNotification[],
): InboxNotification[] {
  return dedupeNotifications([...current, ...nextPage]);
}

function unreadNotificationCount(notifications: InboxNotification[]): number {
  return notifications.reduce(
    (count, notification) => count + Number(notification.readAt === null),
    0,
  );
}

/**
 * Poll the durable inbox with the same visibility-aware, adaptive approach as
 * use-notifications. The legacy session/run polling remains responsible for
 * sidebar state while this hook owns cross-device unread inbox state.
 */
export function useInboxNotifications(
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
  seenRunIdsRef: RefObject<Set<string>>,
  activeSessionIdRef: RefObject<string | null>,
): InboxNotificationAPI {
  const [notifications, setNotifications] = useState<InboxNotification[]>([]);
  const [view, setViewState] = useState<InboxNotificationView>("unread");
  const [unreadCount, setUnreadCountState] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursorState] = useState<string | null>(null);
  const [status, setStatus] = useState<InboxConnectionStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [refreshGeneration, setRefreshGeneration] = useState(0);
  const notificationsRef = useRef<InboxNotification[]>([]);
  const viewRef = useRef<InboxNotificationView>("unread");
  const unreadCountRef = useRef(0);
  const nextCursorRef = useRef<string | null>(null);
  const firstPageIdsRef = useRef<string[]>([]);
  const firstPageCursorRef = useRef<string | null>(null);
  const hasAppendedPagesRef = useRef(false);
  const paginationGenerationRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);
  const hasConnectedRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const activeRefreshesRef = useRef(0);
  const latestRefreshRequestRef = useRef(0);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(typeof document === "undefined" || document.visibilityState === "visible");
  // refresh() is declared before the public action callback below. Keep the
  // latest callback in a ref so refresh can silently invoke the same
  // mark-read-by-session path without making the polling callback circular.
  const markReadBySessionRef = useRef<(sessionId: string) => Promise<boolean>>(
    async () => false,
  );
  // A force refresh or view change can overlap an ordinary poll. Avoid sending
  // duplicate read-by-session writes while the first request is still pending.
  const autoMarkingSessionIdsRef = useRef(new Set<string>());

  const replaceNotifications = useCallback((next: InboxNotification[]) => {
    notificationsRef.current = next;
    setNotifications(next);
  }, []);

  const updateNotifications = useCallback((
    update: (current: InboxNotification[]) => InboxNotification[],
  ): InboxNotification[] => {
    const next = update(notificationsRef.current);
    notificationsRef.current = next;
    setNotifications(next);
    return next;
  }, []);

  const setUnreadCount = useCallback((next: number) => {
    unreadCountRef.current = next;
    setUnreadCountState(next);
  }, []);

  const updateUnreadCount = useCallback((update: (current: number) => number) => {
    const next = update(unreadCountRef.current);
    unreadCountRef.current = next;
    setUnreadCountState(next);
  }, []);

  const setPagination = useCallback((cursor: string | null) => {
    nextCursorRef.current = cursor;
    setNextCursorState(cursor);
  }, []);

  const resetPagination = useCallback((firstPageIds: string[], cursor: string | null) => {
    paginationGenerationRef.current += 1;
    firstPageIdsRef.current = firstPageIds;
    firstPageCursorRef.current = cursor;
    hasAppendedPagesRef.current = false;
    setPagination(cursor);
  }, [setPagination]);

  const setActionFailure = useCallback((message: string) => {
    setError(message);
    setStatus(notificationsRef.current.length > 0 || hasConnectedRef.current ? "stale" : "error");
  }, []);

  const filterSuppressedNotifications = useCallback((
    pageNotifications: InboxNotification[],
  ): { visibleNotifications: InboxNotification[]; suppressedUnreadCount: number } => {
    const activeSessionId = activeSessionIdRef.current;
    const sessionIdsToMarkRead = new Set<string>();
    let suppressedUnreadCount = 0;
    const visibleNotifications = pageNotifications.filter((notification) => {
      // Read items remain available in the All view. Only an unread row can
      // affect the badge or need a server-side read acknowledgement.
      if (notification.readAt !== null || !notification.sessionId) return true;

      const wasSeenViaSse =
        typeof notification.runId === "string"
        && seenRunIdsRef.current?.has(notification.runId) === true;
      const belongsToActiveSession =
        activeSessionId !== null && notification.sessionId === activeSessionId;
      if (!wasSeenViaSse && !belongsToActiveSession) return true;

      sessionIdsToMarkRead.add(notification.sessionId);
      suppressedUnreadCount += 1;
      return false;
    });

    // This intentionally does not await the writes: these rows arrived after
    // the user had already seen the stream (or while viewing its session), so
    // committing them to local state even briefly would flash the unread badge.
    // The next server poll will converge the durable count.
    for (const sessionId of sessionIdsToMarkRead) {
      if (autoMarkingSessionIdsRef.current.has(sessionId)) continue;
      autoMarkingSessionIdsRef.current.add(sessionId);
      void markReadBySessionRef.current(sessionId).finally(() => {
        autoMarkingSessionIdsRef.current.delete(sessionId);
      });
    }

    return { visibleNotifications, suppressedUnreadCount };
  }, [activeSessionIdRef, seenRunIdsRef]);

  const refresh = useCallback(async (force = false): Promise<void> => {
    if (pollInFlightRef.current && !force) return;
    const requestedView = viewRef.current;
    const requestId = ++latestRefreshRequestRef.current;
    activeRefreshesRef.current += 1;
    pollInFlightRef.current = true;
    setLoading(true);
    if (!hasConnectedRef.current && notificationsRef.current.length === 0) {
      setStatus("loading");
    }

    try {
      const [inboxResponse, countResponse] = await Promise.all([
        fetchWithAuth(inboxPageUrl(requestedView), { headers: {} }),
        fetchWithAuth("/api/notifications/unread-count", { headers: {} }),
      ]);

      if (!inboxResponse.ok) throw responseError("Notification inbox", inboxResponse);
      if (!countResponse.ok) throw responseError("Notification unread count", countResponse);

      const [inboxData, countData] = await Promise.all([
        inboxResponse.json() as Promise<InboxPageResponse>,
        countResponse.json() as Promise<{ count?: unknown }>,
      ]);
      const page = inboxPageFromResponse(inboxData);
      const count = Number(countData.count);

      // A view change starts a new request immediately. Ignore a slower,
      // previous-view response so unread data never overwrites the All view.
      if (requestId !== latestRefreshRequestRef.current || requestedView !== viewRef.current) {
        return;
      }

      const firstPageIds = page.notifications.map((notification) => notification.id);
      const { visibleNotifications, suppressedUnreadCount } = filterSuppressedNotifications(
        page.notifications,
      );
      const firstPageChanged = hasAppendedPagesRef.current
        && (
          !sameNotificationIds(firstPageIdsRef.current, firstPageIds)
          || firstPageCursorRef.current !== page.nextCursor
        );

      // Background polls only replace accumulated pages when the first-page
      // membership or cursor changed. With a stable first page, reconcile it
      // and retain appended rows plus their tail cursor so deep scrolling is
      // not jarring. A changed first page resets pagination to avoid stitching
      // a newly ordered head onto a stale cursor chain.
      let nextNotifications: InboxNotification[];
      if (!hasAppendedPagesRef.current || firstPageChanged) {
        nextNotifications = dedupeNotifications(visibleNotifications);
        replaceNotifications(nextNotifications);
        resetPagination(firstPageIds, page.nextCursor);
      } else {
        const pageIdsToReplace = new Set([...firstPageIdsRef.current, ...firstPageIds]);
        nextNotifications = dedupeNotifications([
          ...visibleNotifications,
          ...notificationsRef.current.filter(
            (notification) => !pageIdsToReplace.has(notification.id),
          ),
        ]);
        replaceNotifications(nextNotifications);
        firstPageIdsRef.current = firstPageIds;
        firstPageCursorRef.current = page.nextCursor;
      }

      const serverUnreadCount = Number.isFinite(count) && count >= 0 ? count : 0;
      // The server count is a count of durable event rows. Correct its first
      // page contribution with our session-keyed UI state so repeated runs in
      // one unvisited session produce one badge item, not one per row. Pages
      // beyond the loaded window retain the server total until they are read.
      const rawVisibleUnread = unreadNotificationCount(visibleNotifications);
      const coalescedVisibleUnread = unreadNotificationCount(dedupeNotifications(visibleNotifications));
      setUnreadCount(Math.max(
        0,
        serverUnreadCount - suppressedUnreadCount - rawVisibleUnread + coalescedVisibleUnread,
      ));
      hasConnectedRef.current = true;
      setIsAvailable(true);
      setStatus("ready");
      setError(null);
      setRefreshGeneration((current) => current + 1);
    } catch (err) {
      if (requestId !== latestRefreshRequestRef.current || requestedView !== viewRef.current) {
        throw err;
      }
      const message = err instanceof Error ? err.message : i18n.t("notifications.inbox.refreshFailed");
      setError(message);
      setStatus(notificationsRef.current.length > 0 || hasConnectedRef.current ? "stale" : "error");
      if (!isAuthNotReadyError(err)) {
        console.warn("[inbox] Failed to refresh notifications:", err);
      }
      // Let the scheduler choose a short auth/retryable backoff instead of
      // swallowing the failure and treating a broken inbox as a clean poll.
      throw err;
    } finally {
      activeRefreshesRef.current -= 1;
      pollInFlightRef.current = activeRefreshesRef.current > 0;
      if (activeRefreshesRef.current === 0) {
        setLoading(loadMoreInFlightRef.current);
      }
    }
  }, [
    fetchWithAuth,
    filterSuppressedNotifications,
    replaceNotifications,
    resetPagination,
    setUnreadCount,
  ]);

  const loadMore = useCallback(async (): Promise<void> => {
    const cursor = nextCursorRef.current;
    if (!cursor || loadMoreInFlightRef.current) return;

    const requestedView = viewRef.current;
    const paginationGeneration = paginationGenerationRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    setLoading(true);

    try {
      const inboxResponse = await fetchWithAuth(inboxPageUrl(requestedView, cursor), { headers: {} });
      if (!inboxResponse.ok) throw responseError("Notification inbox", inboxResponse);

      const page = inboxPageFromResponse(await inboxResponse.json() as InboxPageResponse);
      if (
        requestedView !== viewRef.current
        || paginationGeneration !== paginationGenerationRef.current
      ) {
        return;
      }

      const { visibleNotifications, suppressedUnreadCount } = filterSuppressedNotifications(
        page.notifications,
      );
      const currentUnread = unreadNotificationCount(notificationsRef.current);
      const nextNotifications = appendNotifications(notificationsRef.current, visibleNotifications);
      const nextUnread = unreadNotificationCount(nextNotifications);
      replaceNotifications(nextNotifications);
      setPagination(page.nextCursor);
      hasAppendedPagesRef.current = true;
      const rawVisibleUnread = unreadNotificationCount(visibleNotifications);
      // Only decrement for rows that collapse into a session already loaded
      // (or another row in this page); distinct later sessions retain the
      // existing server-derived count.
      const coalescedRows = Math.max(0, currentUnread + rawVisibleUnread - nextUnread);
      if (suppressedUnreadCount > 0 || coalescedRows > 0) {
        updateUnreadCount((current) => Math.max(0, current - suppressedUnreadCount - coalescedRows));
      }
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (
        requestedView !== viewRef.current
        || paginationGeneration !== paginationGenerationRef.current
      ) {
        return;
      }
      const message = err instanceof Error ? err.message : i18n.t("notifications.inbox.loadMoreFailed");
      setActionFailure(message);
      if (!isAuthNotReadyError(err)) {
        console.warn("[inbox] Failed to load more notifications:", err);
      }
      throw err;
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
      if (activeRefreshesRef.current === 0) setLoading(false);
    }
  }, [
    fetchWithAuth,
    filterSuppressedNotifications,
    setActionFailure,
    setPagination,
    replaceNotifications,
    updateUnreadCount,
  ]);

  const setView = useCallback((nextView: InboxNotificationView) => {
    if (nextView === viewRef.current) return;
    viewRef.current = nextView;
    setViewState(nextView);
    // Do not briefly show a previous filter's data beneath the newly selected
    // segment while the replacement request is in flight.
    replaceNotifications([]);
    resetPagination([], null);
    void refresh(true).catch(() => {
      // refresh() has already placed the hook into error/stale state.
    });
  }, [refresh, replaceNotifications, resetPagination]);

  useRefreshOnReconnect(() => refresh(true));

  useEffect(() => {
    let cancelled = false;

    const clearScheduled = () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };

    const schedule = (delay: number) => {
      clearScheduled();
      pollTimeoutRef.current = setTimeout(() => void poll(), delay);
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        await refresh();
        if (cancelled) return;
        schedule(visibleRef.current && unreadCountRef.current > 0 ? TIER_ACTIVE_MS :
          visibleRef.current ? TIER_QUIET_MS : TIER_HIDDEN_MS);
      } catch (err) {
        if (cancelled) return;
        if (isAuthNotReadyError(err)) {
          schedule(TIER_AUTH_NOT_READY_MS);
        } else if (isRetryableInboxError(err)) {
          schedule(TIER_RETRYABLE_ERROR_MS);
        } else {
          schedule(TIER_QUIET_MS);
        }
      }
    };

    const handleVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
      schedule(visibleRef.current ? 0 : TIER_HIDDEN_MS);
    };

    document.addEventListener("visibilitychange", handleVisibility);
    void poll();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      clearScheduled();
    };
  }, [refresh]);

  const refreshAfterLocalAction = useCallback(() => {
    void refresh().catch(() => {
      // refresh() has already placed the hook into error/stale state.
    });
  }, [refresh]);

  const markRead = useCallback(async (notificationId: string): Promise<boolean> => {
    const notification = notificationsRef.current.find(
      (candidate) => candidate.id === notificationId,
    );
    const wasUnread = notification?.readAt === null;
    const currentView = viewRef.current;
    const removesNotification = wasUnread && currentView === "unread";
    const shouldRefillVisiblePage =
      removesNotification && notificationsRef.current.length === 1 && unreadCountRef.current > 1;
    if (wasUnread) {
      const markedReadAt = new Date().toISOString();
      updateNotifications((current) => currentView === "unread"
        ? current.filter((candidate) => candidate.id !== notificationId)
        : current.map((candidate) => candidate.id === notificationId
          ? { ...candidate, readAt: markedReadAt }
          : candidate));
      updateUnreadCount((current) => Math.max(0, current - 1));
    }
    try {
      const response = await fetchWithAuth(
        `/api/notifications/${encodeURIComponent(notificationId)}/read`,
        { method: "POST", headers: {} },
      );
      if (response.ok) {
        if (shouldRefillVisiblePage) refreshAfterLocalAction();
        return true;
      }
      setActionFailure(`Could not mark notification read (${response.status})`);
    } catch (err) {
      if (!isAuthNotReadyError(err)) console.warn("[inbox] Failed to mark notification read:", err);
      setActionFailure("Could not mark notification read");
    }
    refreshAfterLocalAction();
    return false;
  }, [fetchWithAuth, refreshAfterLocalAction, setActionFailure, updateNotifications, updateUnreadCount]);

  const dismiss = useCallback(async (notificationId: string): Promise<boolean> => {
    const notification = notificationsRef.current.find(
      (candidate) => candidate.id === notificationId,
    );
    const wasUnread = notification?.readAt === null;
    const currentView = viewRef.current;
    const shouldRefillVisiblePage =
      Boolean(notification) &&
      currentView === "unread" &&
      notificationsRef.current.length === 1 &&
      unreadCountRef.current > (wasUnread ? 1 : 0);
    if (notification) {
      updateNotifications((current) => current.filter((candidate) => candidate.id !== notificationId));
      if (wasUnread) {
        updateUnreadCount((current) => Math.max(0, current - 1));
      }
    }
    try {
      const response = await fetchWithAuth(
        `/api/notifications/${encodeURIComponent(notificationId)}/dismiss`,
        { method: "POST", headers: {} },
      );
      if (response.ok) {
        if (shouldRefillVisiblePage) refreshAfterLocalAction();
        return true;
      }
      setActionFailure(`Could not dismiss notification (${response.status})`);
    } catch (err) {
      if (!isAuthNotReadyError(err)) console.warn("[inbox] Failed to dismiss notification:", err);
      setActionFailure("Could not dismiss notification");
    }
    refreshAfterLocalAction();
    return false;
  }, [fetchWithAuth, refreshAfterLocalAction, setActionFailure, updateNotifications, updateUnreadCount]);

  const markReadBySession = useCallback(async (sessionId: string): Promise<boolean> => {
    const unreadNotifications = notificationsRef.current.filter(
      (notification) => notification.sessionId === sessionId && notification.readAt === null,
    );
    const markedReadCount = unreadNotifications.length;
    const currentView = viewRef.current;
    const shouldRefillVisiblePage =
      currentView === "unread" &&
      markedReadCount > 0 &&
      markedReadCount === notificationsRef.current.length &&
      unreadCountRef.current > markedReadCount;
    if (markedReadCount > 0) {
      const markedReadAt = new Date().toISOString();
      updateNotifications((current) => currentView === "unread"
        ? current.filter(
          (notification) => notification.sessionId !== sessionId || notification.readAt !== null,
        )
        : current.map((notification) =>
          notification.sessionId === sessionId && notification.readAt === null
            ? { ...notification, readAt: markedReadAt }
            : notification,
        ));
      updateUnreadCount((current) => Math.max(0, current - markedReadCount));
    }
    try {
      const response = await fetchWithAuth("/api/notifications/read-by-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (response.ok) {
        if (shouldRefillVisiblePage) refreshAfterLocalAction();
        return true;
      }
      setActionFailure(`Could not mark session notifications read (${response.status})`);
    } catch (err) {
      if (!isAuthNotReadyError(err)) console.warn("[inbox] Failed to mark session read:", err);
      setActionFailure("Could not mark session notifications read");
    }
    refreshAfterLocalAction();
    return false;
  }, [fetchWithAuth, refreshAfterLocalAction, setActionFailure, updateNotifications, updateUnreadCount]);
  markReadBySessionRef.current = markReadBySession;

  const hasMore = nextCursor !== null;
  const fetchScheduleHistory = useCallback((url: string) => {
    if (!/^\/api\/agents\/[^/]+\/schedules\/[^/]+\/runs$/.test(url)) {
      throw new Error("Invalid schedule history URL");
    }
    return fetchWithAuth(`${url}?limit=500&page=1`, { method: "GET" });
  }, [fetchWithAuth]);
  return useMemo(() => ({
    notifications,
    fetchScheduleHistory,
    view,
    setView,
    unreadCount,
    loading,
    loadingMore,
    nextCursor,
    hasMore,
    loadMore,
    status,
    error,
    isAvailable,
    refreshGeneration,
    refresh,
    markRead,
    dismiss,
    markReadBySession,
  }), [
    notifications,
    fetchScheduleHistory,
    view,
    setView,
    unreadCount,
    loading,
    loadingMore,
    nextCursor,
    hasMore,
    loadMore,
    status,
    error,
    isAvailable,
    refreshGeneration,
    refresh,
    markRead,
    dismiss,
    markReadBySession,
  ]);
}
